import { IconDragDrop } from "@tabler/icons-react";
import type { UploadFile } from "antd";
import { Form, Input, Upload } from "antd";
import { useState } from "react";
import {
	useConfirmAsset,
	useRegisterAsset,
} from "@/common/artemis-operations/assets";
import type { ParentType } from "@/common/artemis-sdk/graphql";
import {
	ASSET_UPLOAD_ERROR_DESC,
	ASSET_UPLOAD_ERROR_TITLE,
	ASSET_UPLOAD_SUCCESS_DESC,
	ASSET_UPLOAD_SUCCESS_TITLE,
} from "@/common/messages";
import { AssetCategory } from "@/common/model";
import { uploadToPresignedPost } from "@/common/presigned-upload";
import { useNotify } from "@/common/use-notify";
import { Wizard } from "@/components/wizard";

interface UploadAssetDialogProps {
	open: boolean;
	onClose: () => void;
	parentId: string;
	parentType: ParentType;
	workspaceId: string;
	category?: AssetCategory;
	onUploadComplete?: () => void;
}

interface UploadFormValues {
	file?: UploadFile[];
	name?: string;
	description?: string;
}

/** Drawer wizard for uploading a file asset. Supports drag-and-drop. */
export function UploadAssetDialog({
	open,
	onClose,
	parentId,
	parentType,
	workspaceId,
	category = AssetCategory.BINARY,
	onUploadComplete,
}: UploadAssetDialogProps) {
	const [form] = Form.useForm<UploadFormValues>();
	const notify = useNotify();
	const [uploading, setUploading] = useState(false);
	const { mutateAsync: registerAsset } = useRegisterAsset();
	const { mutateAsync: confirmAsset } = useConfirmAsset();

	const reset = () => {
		form.resetFields();
		setUploading(false);
	};

	const handleComplete = async () => {
		try {
			const values = await form.validateFields();
			const file = values.file?.[0]?.originFileObj as File | undefined;
			if (!file) return;

			setUploading(true);
			try {
				// 1. Register — get assetId + presigned staging POST
				const { asset, upload } = await registerAsset({
					name: values.name || file.name,
					description: values.description ?? "",
					mediaType: file.type || "application/octet-stream",
					category: category,
					filename: file.name,
					parentId,
					parentType,
					workspaceId,
				});

				// 2. POST the file to the presigned staging endpoint.
				await uploadToPresignedPost(upload, file);

				// 3. Confirm — copy staging → live, activate the asset
				await confirmAsset(asset.id);

				notify.success(ASSET_UPLOAD_SUCCESS_TITLE, ASSET_UPLOAD_SUCCESS_DESC);
				onUploadComplete?.();
				onClose();
				reset();
			} catch {
				notify.error(ASSET_UPLOAD_ERROR_TITLE, ASSET_UPLOAD_ERROR_DESC);
				setUploading(false);
			}
		} catch {
			// form validation failed — stay open
		}
	};

	return (
		<Wizard
			open={open}
			title="Upload Asset"
			completeLabel="Upload"
			form={form}
			loading={uploading}
			onComplete={handleComplete}
			onCancel={() => {
				reset();
				onClose();
			}}
			pages={[
				{
					id: "upload",
					title: "Upload",
					content: (
						<>
							<Form.Item
								name="file"
								label="File"
								valuePropName="fileList"
								getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
								rules={[{ required: true, message: "Select a file" }]}
							>
								<Upload.Dragger beforeUpload={() => false} maxCount={1}>
									<p className="ant-upload-drag-icon">
										<IconDragDrop />
									</p>
									<p className="ant-upload-text">
										Click or drag a file here to upload
									</p>
								</Upload.Dragger>
							</Form.Item>

							<Form.Item name="name" label="Name">
								<Input placeholder="Leave blank to use filename" />
							</Form.Item>

							<Form.Item name="description" label="Description">
								<Input.TextArea rows={2} placeholder="Optional description" />
							</Form.Item>
						</>
					),
				},
			]}
		/>
	);
}
